import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import RegisterForm from '../../components/Auth/RegisterForm';
import { useAuth } from '../../contexts/AuthContext';
import type { RegisterData } from '../../services/authService';
import { logger } from '../../utils/logger';

const RegisterPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (data: RegisterData) => {
    setIsLoading(true);
    setError('');

    try {
      logger.log('[RegisterPage] Iniciando registro...');
      const response = await register({
        email: data.email,
        password: data.password,
        passwordConfirm: data.passwordConfirm,
        firstName: data.firstName,
        lastName: data.lastName,
        termsAccepted: data.termsAccepted
      });
      
      // Sucesso - redirecionar para verificação de código (email)
      logger.log('[RegisterPage] Registro bem-sucedido:', response);
      logger.log('[RegisterPage] Redirecionando para verify-email...');

      setError('');
      setIsLoading(false);

      const verifyUrl = `/verify-email?email=${encodeURIComponent(data.email)}`;
      logger.log('[RegisterPage] Navegando para:', verifyUrl);

      try {
        navigate(verifyUrl, { replace: true });
        setTimeout(() => {
          if (window.location.pathname !== '/verify-email') {
            logger.log('[RegisterPage] Usando window.location como fallback');
            window.location.href = verifyUrl;
          }
        }, 500);
      } catch (navError) {
        logger.error('[RegisterPage] Erro ao navegar, usando window.location:', navError);
        window.location.href = verifyUrl;
      }
    } catch (err) {
      logger.error('[RegisterPage] Erro ao registrar:', err);
      
      // Se for timeout, verificar se a conta foi criada mesmo assim
      if (err instanceof Error && err.message.includes('Timeout')) {
        logger.log('[RegisterPage] Timeout detectado, verificando se conta foi criada...');
        
        // Aguardar um pouco e então redirecionar (assumindo que foi criada)
        // O backend já processou a requisição mesmo que a resposta não tenha chegado
        setTimeout(() => {
          logger.log('[RegisterPage] Redirecionando apesar do timeout (conta provavelmente criada)');
          setIsLoading(false);
          const verifyUrl = `/verify-email?email=${encodeURIComponent(data.email)}`;
          window.location.href = verifyUrl;
        }, 1000);
        return;
      }
      
      setIsLoading(false);
      // Tratar diferentes tipos de erro
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'detail' in err) {
        setError((err as any).detail);
      } else {
        setError('Erro ao criar conta. Tente novamente.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Content */}
      <div className="w-full max-w-2xl">
        {/* Full navigation: / is static HTML landing, not a React route */}
        <a
          href="/"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-secondary transition-colors hover:text-secondary/80"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Voltar para a página principal
        </a>
        <RegisterForm 
          onSubmit={handleRegister}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </div>
  );
};

export default RegisterPage;
